import React, { useEffect, useMemo, useState } from 'react'
import { Check, Loader2, MapPin, Search, X } from 'lucide-react'
import { propertiesService } from '../services/propertiesService'

const count = (value) => value == null ? '—' : Number(value).toLocaleString('cs-CZ')
const same = (left, right) => String(left || '').localeCompare(String(right || ''), 'cs', { sensitivity: 'base' }) === 0

const SelectedChips = ({ values, onRemove }) => values.length > 0 && (
  <div className="flex flex-wrap gap-2">
    {values.map((value) => (
      <button key={value} type="button" onClick={() => onRemove(value)} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100">
        {value}<X className="h-3 w-3" />
      </button>
    ))}
  </div>
)

const FacetList = ({ items, selected, onToggle, search, emptyText }) => {
  const visible = items.filter(({ name }) => name.toLocaleLowerCase('cs-CZ').includes(search.toLocaleLowerCase('cs-CZ'))).slice(0, 100)
  return (
    <div className="mt-2 max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white">
      {visible.map((item) => {
        const active = selected.some((value) => same(value, item.name))
        return (
          <button key={item.name} type="button" onClick={() => onToggle(item.name)} className={`flex w-full items-center gap-3 border-b border-slate-100 px-3 py-2.5 text-left last:border-0 ${active ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${active ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300'}`}>{active && <Check className="h-3.5 w-3.5" />}</span>
            <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{item.name}</span>
            <span className="text-xs tabular-nums text-slate-400">{count(item.count)}</span>
          </button>
        )
      })}
      {!visible.length && <div className="px-4 py-8 text-center text-sm text-slate-400">{emptyText}</div>}
      {items.length > 100 && !search && <div className="border-t px-3 py-2 text-xs text-slate-400">Pro další obce použijte hledání.</div>}
    </div>
  )
}

const LocationFilter = ({ propertyType, offerType, regions, region, onRegionChange, municipalities, onMunicipalitiesChange, parts, onPartsChange }) => {
  const [facets, setFacets] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [municipalitySearch, setMunicipalitySearch] = useState('')
  const [partSearch, setPartSearch] = useState('')

  useEffect(() => {
    let current = true
    setLoading(true)
    propertiesService.fetchLocationFacets({ offerType, type: propertyType, region, municipalities })
      .then((data) => { if (current) { setFacets(data); setError('') } })
      .catch((requestError) => { if (current) setError(requestError.message || 'Lokality se nepodařilo načíst.') })
      .finally(() => { if (current) setLoading(false) })
    return () => { current = false }
  }, [offerType, propertyType, region, municipalities])

  const regionCounts = useMemo(() => Object.fromEntries((facets?.regions || []).map((item) => [item.key, item.count])), [facets])
  const quickCounts = useMemo(() => Object.fromEntries((facets?.quickLocations || []).map((item) => [item.name, item.count])), [facets])
  const regionalOptions = regions.filter((item) => item.kind === 'region')
  const selection = facets?.selection

  const chooseRegion = (value) => {
    onRegionChange(value)
    onMunicipalitiesChange([])
    onPartsChange([])
    setMunicipalitySearch('')
    setPartSearch('')
  }
  const toggleMunicipality = (name) => {
    const next = municipalities.some((value) => same(value, name)) ? municipalities.filter((value) => !same(value, name)) : [...municipalities, name]
    onMunicipalitiesChange(next)
    onPartsChange([])
  }
  const togglePart = (name) => onPartsChange(parts.some((value) => same(value, name)) ? parts.filter((value) => !same(value, name)) : [...parts, name])

  return (
    <div className="space-y-5">
      <div className="space-y-4">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Rychlá volba</div>
          <div className="grid grid-cols-3 gap-2">
            {[{ value: 'all', label: 'Celá ČR', total: facets?.total }, { value: 'Brno', label: 'Brno', total: quickCounts.brno }, { value: 'Ostrava', label: 'Ostrava', total: quickCounts.ostrava }].map((item) => (
              <button key={item.value} type="button" onClick={() => chooseRegion(item.value)} className={`flex items-center justify-between rounded-xl border px-3 py-2.5 text-left ${same(region, item.value) ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                <span className="truncate text-sm font-medium">{item.label}</span><span className="ml-2 text-xs tabular-nums text-slate-400">{count(item.total)}</span>
              </button>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Kraj</span>
          <select value={regionalOptions.some((item) => same(item.value, region)) ? region : ''} onChange={(event) => chooseRegion(event.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
            <option value="" disabled>Vyberte kraj</option>
            {regionalOptions.map((item) => <option key={item.value} value={item.value}>{item.label} · {count(regionCounts[item.value])}</option>)}
          </select>
        </label>
        <div className="rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-500">
          Počty odpovídají zvolenému typu nemovitosti a druhu nabídky.
        </div>
      </div>

      <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-600" /><h4 className="text-sm font-semibold text-slate-900">Upřesnit lokalitu</h4></div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
        </div>
        {error && <div className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-700">{error}</div>}
        {selection?.kind === 'all' && <div className="flex min-h-48 items-center justify-center px-6 text-center text-sm leading-6 text-slate-500">Vyberte kraj, Brno nebo Ostravu. Potom můžete filtrovat konkrétní obec a její části.</div>}

        {selection?.kind === 'region' && <>
          <div className="mt-4 flex items-center justify-between"><span className="text-sm font-medium text-slate-700">Město / obec</span><span className="text-xs text-slate-400">{municipalities.length ? `${municipalities.length} vybráno` : `${facets?.municipalities?.length || 0} možností`}</span></div>
          <div className="relative mt-2"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={municipalitySearch} onChange={(event) => setMunicipalitySearch(event.target.value)} placeholder="Hledat město nebo obec…" className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" /></div>
          <div className="mt-2"><SelectedChips values={municipalities} onRemove={toggleMunicipality} /></div>
          <FacetList items={facets?.municipalities || []} selected={municipalities} onToggle={toggleMunicipality} search={municipalitySearch} emptyText="Žádná obec s nabídkami." />
        </>}

        {(selection?.kind === 'quick' || municipalities.length === 1) && <>
          <div className="mt-5 flex items-center justify-between"><span className="text-sm font-medium text-slate-700">Městská část / část obce</span><span className="text-xs text-slate-400">{parts.length ? `${parts.length} vybráno` : `${facets?.parts?.length || 0} možností`}</span></div>
          <div className="relative mt-2"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={partSearch} onChange={(event) => setPartSearch(event.target.value)} placeholder="Hledat část obce…" className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" /></div>
          <div className="mt-2"><SelectedChips values={parts} onRemove={togglePart} /></div>
          <FacetList items={facets?.parts || []} selected={parts} onToggle={togglePart} search={partSearch} emptyText="Žádné části obce s nabídkami." />
        </>}
        {selection?.kind === 'region' && municipalities.length > 1 && <div className="mt-4 rounded-xl border border-dashed border-slate-300 p-3 text-xs text-slate-500">Pro výběr části obce ponechte právě jednu obec.</div>}
      </div>
    </div>
  )
}

export default LocationFilter
